import React from 'react'
import { Map, List } from 'immutable'
import _compact from 'lodash/compact'
import _uniq from 'lodash/uniq'
import _map from 'lodash/map'
import {
  TYPE_CONTENT_SERIES,
  TYPE_TAG_YOGA_STYLE,
  TYPE_TAG_YOGA_LEVEL,
  TYPE_TAG_YOGA_TEACHER,
  TYPE_TAG_FITNESS_STYLE,
  TYPE_TAG_FITNESS_LEVEL,
  TYPE_TAG_FITNESS_INSTRUCTOR,
  TYPE_TAG_MEDITATION_FOCUS,
  SUBTYPE_CONTENT_MEDITATION,
  isGuideType,
} from 'services/content-type'
import {
  formatDuration,
} from 'theme/web-app'
import Link from 'components/Link'
import TextSeasonEpisode from 'components/TextSeasonEpisode'
import {
  getTileV2MobileMetadataClass,
  handleTileV2OnClick,
  getTileUrl,
} from './utils'

/**
 * Renders the season and episode numbers
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @param {Boolean} props.isGuide - Whether or not the content is a guide
 * @returns {String|null} - If seasonNumber and episodeNumber are null, return null.
 *  Otherwise return the formatted season and episode numbers
 */
function SeasonEpisodeNumbers (props) {
  const { seasonNumber, episodeNumber, isGuide } = props

  return (
    <TextSeasonEpisode
      className={['tile-v2__mobile-season-episode-numbers']}
      season={seasonNumber}
      episode={episodeNumber}
      isGuide={isGuide}
    />
  )
}

/**
 * Series season episode in the "Series Title: S1:E1" format
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {JSX.Element|null} Series title combined with the season and episode number
 */
function SeriesSeasonEpisode (props) {
  const {
    seasonNumber,
    episodeNumber,
    isGuide,
  } = props

  if (!seasonNumber || !episodeNumber) {
    return null
  }

  return (
    <span className="tile-v2__mobile-series-season-episode">
      <SeasonEpisodeNumbers
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
      />
    </span>
  )
}

function MobileMetadataTop (props) {
  const {
    data,
    featuredEpisode,
    contentType,
    userLanguage,
  } = props
  const tags = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('references', List()) : data.get('references', List()) || List()
  const yogaTeacherTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_TEACHER && t.get('language') === userLanguage) || Map()
  const fitnessInstructorTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_INSTRUCTOR && t.get('language') === userLanguage) || Map()
  const showYogaFitness = yogaTeacherTag.size || fitnessInstructorTag.size
  const showMetadataTop = showYogaFitness
    || contentType === TYPE_CONTENT_SERIES
    || contentType === 'video'

  if (!showMetadataTop) {
    return null
  }

  const seriesTitle = contentType === 'video' ? data.get('seriesTitle') : data.get('title')
  const seriesUrl = contentType === 'video' ? data.get('seriesPath') : getTileUrl(props)
  const yogaTeacher = yogaTeacherTag.get('title', '')
  const yogaTeacherUrl = yogaTeacherTag.get('path', '')
  const fitnessInstructor = fitnessInstructorTag.get('title', '')
  const fintessInstructorUrl = fitnessInstructorTag.get('path', '')
  const featuredEpisodeNumber = featuredEpisode.get('episodeNumber', 0)
  const featuredEpisodeSeasonNumber = featuredEpisode.get('seasonNumber', 0)
  const isGuide = isGuideType(data)

  if (fitnessInstructorTag.size) {
    return (
      <div className="tile-v2__mobile-metadata-top">
        <Link
          to={fintessInstructorUrl}
          className="tile-v2__mobile-metadata-top-link"
          onClick={(e) => handleTileV2OnClick(e, props)}
        >
          {fitnessInstructor}A
        </Link>
      </div>
    )
  }

  if (yogaTeacherTag.size) {
    return (
      <div className="tile-v2__mobile-metadata-top">
        <Link
          to={yogaTeacherUrl}
          className="tile-v2__mobile-metadata-top-link"
          onClick={(e) => handleTileV2OnClick(e, props)}
        >
          {yogaTeacher}backdrop
        </Link>
      </div>
    )
  }

  if (featuredEpisode.size || (seriesTitle && seriesUrl)) {
    return (
      <div className="tile-v2__mobile-metadata-top">
        {
          featuredEpisode.size
            ? <SeriesSeasonEpisode
              seasonNumber={featuredEpisodeSeasonNumber}
              episodeNumber={featuredEpisodeNumber}
              isGuide={isGuide}
            />
            : null
        }
        {
          seriesTitle && seriesUrl && (
            <Link
              to={seriesUrl}
              className="tile-v2__mobile-metadata-top-link"
              onClick={(e) => handleTileV2OnClick(e, props)}
            >
              {seriesTitle}
            </Link>
          )
        }
      </div>
    )
  }

  return null
}

function MobileMetadataBottom (props) {
  const {
    data,
    featuredEpisode,
    userLanguage,
    staticText,
  } = props
  const contentSubtype = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('contentSubtype') : data.get('contentSubtype')
  const tags = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('references', List()) : data.get('references', List()) || List()
  const yogaStyleTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_STYLE && t.get('language') === userLanguage) || Map()
  const yogaLevelTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_LEVEL && t.get('language') === userLanguage) || Map()
  const fitnessStyleTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_STYLE && t.get('language') === userLanguage) || Map()
  const fitnessLevelTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_LEVEL && t.get('language') === userLanguage) || Map()
  const meditationFocusTag = tags.find((t) => t.get('type') === TYPE_TAG_MEDITATION_FOCUS && t.get('language') === userLanguage) || Map()
  const featuredMedia = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('feature', Map()) : data.get('feature', Map())
  const duration = featuredMedia.get('duration', 0) || 0
  const yogaStyleTitle = yogaStyleTag.get('title')
  const yogaLevelTitle = yogaLevelTag.get('title')
  const fitnessStyleTitle = fitnessStyleTag.get('title')
  const fitnessLevelTitle = fitnessLevelTag.get('title')
  const meditationFocusTitle = meditationFocusTag.get('title')

  const renderMetadataItem = (item, classModifier) => {
    let className = 'tile-v2__mobile-metadata-item'

    if (classModifier) {
      className += ` ${className}--${classModifier}`
    }

    return (
      <span className={className} key={item}>
        {item}
      </span>
    )
  }

  const metadata = duration > 0 ? [formatDuration(duration)] : []

  if (contentSubtype === SUBTYPE_CONTENT_MEDITATION) {
    metadata.push(staticText.get('meditation'))
  }

  metadata.push(
    yogaStyleTitle,
    yogaLevelTitle,
    fitnessStyleTitle,
    fitnessLevelTitle,
    meditationFocusTitle,
  )

  return (
    <div className="tile-v2__mobile-metadata-bottom">
      {
        _map(_uniq(_compact(metadata)), (i) => {
          return (
            renderMetadataItem(i)
          )
        })
      }
    </div>
  )
}

/**
 * The title rendered as a link
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {String} props.url - The url of the content
 * @returns {JSX.Element|null} - The content title as a link
 */
function TitleLink (props) {
  const {
    title,
    url,
  } = props

  if (!title || !url) {
    return null
  }

  return (
    <Link
      to={url}
      className="tile-v2__mobile-title"
      onClick={(e) => handleTileV2OnClick(e, props)}
    >
      {title}
    </Link>
  )
}

function TileV2MobileMetadata (props) {
  const {
    data,
    displayMode,
    isTouchable,
    userNodeInfo,
  } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)
  const featuredEpisode = Map.isMap(userNodeInfo) ? userNodeInfo.getIn(['featuredEpisode', 'data'], Map()) : Map()
  const featuredEpisodeInProgress = featuredEpisode.getIn(['feature', 'userMediaPosition', 'position'], 0) > 0
  const featuredEpisodeTitle = featuredEpisode.get('title', '')
  const featuredEpisodeUrl = featuredEpisode.get('path', '')

  if (!isTouchable) {
    return null
  }

  return (
    <div className={getTileV2MobileMetadataClass({ baseClassName: 'tile-v2__mobile-metadata', displayMode })}>
      <MobileMetadataTop {...props} featuredEpisode={featuredEpisode} />
      <TitleLink
        {...props}
        url={featuredEpisodeInProgress ? featuredEpisodeUrl : url}
        title={featuredEpisodeInProgress ? featuredEpisodeTitle : title}
      />
      <MobileMetadataBottom {...props} featuredEpisode={featuredEpisode} />
    </div>
  )
}

export default TileV2MobileMetadata
